import type {
  ApolloClient,
  DocumentNode,
  ErrorLike,
  OperationVariables,
  SubscriptionObservable,
  TypedDocumentNode,
} from '@apollo/client';
import type { useSubscription as apolloUseSubscription } from '@apollo/client/react';
import { useApolloClient } from '@apollo/client/react';
// oxlint-disable-next-line sorare/no-extraneous-dependencies
import { equal } from '@wry/equality';
import { useEffect, useRef, useState } from 'react';

// Not apollo's useSubscription.Result: that one requires a `restart` this fork
// never produced, and dropped the `variables` consumers here still read.
type Result<TData, TVariables extends OperationVariables> = {
  loading: boolean;
  data?: TData;
  error?: ErrorLike;
  variables?: TVariables;
};

// Inlined from https://github.com/apollographql/apollo-client/blob/main/src/react/hooks/useSubscription.ts
// With the following change
//
// Subscribe in an effect instead of in useState initializer, as the initializer
// can run multiple times when react is in concurrent mode

export type SubscriptionOptions<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = apolloUseSubscription.Options<TData, TVariables>;

export function useSubscription<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  subscription: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: apolloUseSubscription.Options<NoInfer<TData>, NoInfer<TVariables>>
) {
  const client = useApolloClient(options?.client);
  const [result, setResult] = useState<Result<TData, TVariables>>({
    loading: !options?.skip,
    error: undefined,
    data: undefined,
    variables: options?.variables,
  });

  const [observable, setObservable] = useState<null | SubscriptionObservable<
    ApolloClient.SubscribeResult<TData>
  >>(null);

  const canResetObservableRef = useRef(true);
  useEffect(() => {
    return () => {
      canResetObservableRef.current = true;
    };
  }, []);

  const ref = useRef({ client, subscription, options });
  useEffect(() => {
    let shouldResubscribe = options?.shouldResubscribe;
    if (typeof shouldResubscribe === 'function') {
      shouldResubscribe = !!shouldResubscribe(options!);
    }

    if (options?.skip) {
      if (
        !options?.skip !== !ref.current.options?.skip ||
        canResetObservableRef.current
      ) {
        // oxlint-disable-next-line react/set-state-in-effect
        setResult({
          loading: false,
          data: undefined,
          error: undefined,
          variables: options?.variables,
        });
        setObservable(null);
        canResetObservableRef.current = false;
      }
    } else if (
      (shouldResubscribe !== false &&
        (client !== ref.current.client ||
          subscription !== ref.current.subscription ||
          options?.fetchPolicy !== ref.current.options?.fetchPolicy ||
          !options?.skip !== !ref.current.options?.skip ||
          !equal(options?.variables, ref.current.options?.variables))) ||
      canResetObservableRef.current
    ) {
      setResult({
        loading: true,
        data: undefined,
        error: undefined,
        variables: options?.variables,
      });
      setObservable(
        client.subscribe({
          query: subscription,
          variables: options?.variables,
          fetchPolicy: options?.fetchPolicy,
          context: options?.context,
        } as ApolloClient.SubscribeOptions<TData, TVariables>)
      );
      canResetObservableRef.current = false;
    }

    Object.assign(ref.current, { client, subscription, options });
  }, [client, subscription, options]);

  useEffect(() => {
    if (!observable) {
      return;
    }

    let subscriptionStopped = false;
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const subscription = observable.subscribe({
      next(fetchResult) {
        if (subscriptionStopped) {
          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-shadow
        const result = {
          loading: false,
          // TODO: fetchResult.data can be null but SubscriptionResult.data
          // expects TData | undefined only
          data: fetchResult.data!,
          error: undefined,
          variables: options?.variables,
        };
        setResult(result);

        if (ref.current.options?.onData) {
          ref.current.options.onData({
            client,
            data: result,
          });
        }
      },
      error(error) {
        if (!subscriptionStopped) {
          setResult({
            loading: false,
            data: undefined,
            error,
            variables: options?.variables,
          });
          ref.current.options?.onError?.(error);
        }
      },
      complete() {
        if (!subscriptionStopped) {
          if (ref.current.options?.onComplete) {
            ref.current.options.onComplete();
          }
        }
      },
    });

    // eslint-disable-next-line consistent-return
    return () => {
      subscriptionStopped = true;
      subscription.unsubscribe();
    };
    // oxlint-disable-next-line react/exhaustive-deps
  }, [observable]);

  return result;
}
